import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { TemplateDetail } from '../components/esign/TemplateDetail';
import { esignAPI, errMsg } from '../lib/esignAPI';

const NewTemplate = ({ onCreated }) => {
  const [f, setF] = useState({ name: '', description: '', file: null });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const fd = new FormData();
    fd.append('name', f.name); fd.append('description', f.description); fd.append('file', f.file);
    setBusy(true);
    try { const t = await esignAPI.createTemplate(fd); toast.success('Template uploaded'); onCreated(t.key); setF({ name: '', description: '', file: null }); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Card data-testid="tpl-new-card"><CardHeader><CardTitle className="flex items-center gap-2 text-base"><Upload className="h-4 w-4" />Upload DOCX / PDF template</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <Input placeholder="Template name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tpl-new-name" />
        <Input placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="tpl-new-description" />
        <Input type="file" accept=".docx,.pdf" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="tpl-new-file" />
        <p className="text-xs text-muted-foreground">DOCX: use {'{{variables}}'} from the picker; converted to PDF with LibreOffice. PDF: attached as a static document. Uploaded templates and new versions can be used straight away; letters already issued keep the version they were sent with.</p>
        <Button disabled={busy || f.name.trim().length < 3 || !f.file} onClick={submit} data-testid="tpl-new-submit">Upload template</Button>
      </CardContent>
    </Card>
  );
};

export default function EsignTemplates({ embedded = false }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => esignAPI.templates().then((r) => { setData(r); setSel((s) => s || r.templates[0]?.key); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const cur = data.templates.find((t) => t.key === sel);
  return (
    <div className={embedded ? 'space-y-6' : 'space-y-6 p-6'} data-testid="esign-templates-page">
      {!embedded && <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Engagement letter templates</h1>
        <p className="mt-1 text-sm text-muted-foreground">Issued letters keep the exact template version they were generated from.</p>
      </div>}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-4">
          <Card><CardContent className="space-y-1 p-2" data-testid="tpl-list">
            {data.templates.map((t) => (
              <button key={t.key} type="button" onClick={() => setSel(t.key)} data-testid={`tpl-item-${t.key}`}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${sel === t.key ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}>
                <FileText className="h-4 w-4 shrink-0" /><span className="flex-1 truncate">{t.name}</span>
                <Badge className={t.active ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'}>{t.active ? 'Active' : 'Inactive'}</Badge>
              </button>))}
          </CardContent></Card>
          <NewTemplate onCreated={(k) => { setSel(k); load(); }} />
        </div>
        {cur && <TemplateDetail key={cur.key} t={cur} meta={data} onChanged={load} />}
      </div>
    </div>
  );
}
